<template>
  <div class="input-amount-container">
    <div class="amount__row">
      <p class="amount__title">{{ getTranslatedText("Bet Amount") }}:</p>
      <div class="amount__input">
        <p class="amount__input--currency">{{ currencySymbol }}</p>
        <input
          :class="`amount__input--value ${
            className !== '' ? ` ${className}` : ''
          }`"
          class="amount__input--value"
          :value="toFixNumber(amountValue, 2)"
          @keydown="validateInput"
          @focus="isEditing = true"
          @blur="isEditing = false"
          @change="handleInputChange"
        />
      </div>
    </div>
    <ul class="amount__list" v-if="betAmoutValues.length > 0">
        <li
          v-for="amount in betAmoutValues"
          :key="amount"
          class="amount__option"
          @click="chooseAmountValue(amount)"
        >
          +{{ amount }}
        </li>

        <li class="amount__option cls" @click="chooseAmountValue(0, true)">C</li>
    </ul>
  </div>
</template>
<script>
import languageMixins from "@/mixins/language-mixins";
import utilsMixin from "@/mixins/function-utils";
import { getStateStore } from "@/utils/utils.ts";
import { clampWagerByBetSettings } from "@/utils/betslip-transform";

export default {
  mixins: [languageMixins, utilsMixin],
  props: {
    className: {
      type: String,
    },
    isDisable: {
      type: Boolean,
    },
    itemId: {
      type: [Number, String],
    },
    amountDefault: {
      type: Number,
      default: 0,
    },
    // settings include bet_min, bet_max, bet_amount_values, bet_default, payout_max
    settings: {
      type: Object,
      default: () => ({}),
    },
    odds: {
      type: Number,
      default: 0,
    },
  },
  watch: {
    amountDefault: "syncAmountFromProps",
    itemId: "syncAmountFromProps",
    // Not deep: in the combination market `settings` and `amountDefault` are the same
    // object (`outcome` / `outcome.amount`). A deep watch re-fires on every
    // `outcome.amount` write that the `amountDefault` watcher already handles,
    // causing a redundant re-sync loop (page freeze) on amount changes such as Clear "C".
    settings: "syncAmountFromProps",
  },
  data() {
    return {
      betAmoutValues: [],
      amountValue: 0,
      currencySymbol: "",
      timer: null,
      isEditing: false,
      skipPropSync: false,
    };
  },
  computed: {
    isActive() {
      return this.$store.state.stateAction.activeAmountItemId === this.itemId;
    },
  },
  created() {
    this.syncAmountFromProps();
  },
  methods: {
    getActiveSettings() {
      if (this.settings && Object.keys(this.settings).length > 0) {
        return this.settings;
      }

      const { accountSetting } = getStateStore();
      return accountSetting;
    },

    syncAmountFromProps() {
      if (this.isEditing) {
        return;
      }
      if (this.skipPropSync) {
        this.skipPropSync = false;
        return;
      }
      const nextAmount = this.clampAmount(this.amountDefault);
      if (nextAmount === this.amountValue && this.betAmoutValues.length > 0) {
        return;
      }
      const { bet_amount_values } = this.getActiveSettings();
      this.betAmoutValues = bet_amount_values ?? [];
      this.currencySymbol = this.$store.state.accountSetting.currency_symbol;
      this.amountValue = nextAmount;
    },

    handleShowSelectItem(event) {
      this.$store.commit(
        "setActiveAmountItemId",
        this.isActive ? null : this.itemId
      );

      const parentElement = event.target.parentElement;
      const element = parentElement.closest(".betslip-table__row");
      const combinationElement = parentElement.closest(
        ".combination-outcome__item"
      );

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "center",
          });
        });
      } else if (combinationElement) {
        setTimeout(() => {
          const elementPosition =
            combinationElement.getBoundingClientRect().top + window.pageYOffset;
          const elementHeight =
            combinationElement.getBoundingClientRect().height;
          const windowHeight = window.innerHeight;

          const scrollPosition =
            elementPosition - (windowHeight / 2 - elementHeight / 2) + 20;
          const pageHeight = document.documentElement.scrollHeight;
          const maxScroll = pageHeight - windowHeight;

          const finalScrollPosition = Math.min(scrollPosition, maxScroll);

          window.scrollTo({
            top: finalScrollPosition,
            behavior: "smooth",
          });
        });
      }
    },
    clampAmount(amount) {
      return clampWagerByBetSettings(
        amount,
        this.odds,
        this.getActiveSettings()
      );
    },

    emitAmountValue() {
      this.$emit("amount-value", {
        amount: this.amountValue,
        itemId: this.itemId,
      });
    },

    chooseAmountValue(amount, replace = false) {
      if (this.$store.state.stateAction.activeAmountItemId === this.itemId) {
        this.$store.commit("setActiveAmountItemId", null);
      }

      if (replace) {
        this.amountValue = amount;
      } else {
        this.amountValue += amount;
      }

      this.amountValue = this.clampAmount(this.amountValue);
      this.skipPropSync = true;
      this.emitAmountValue();
    },

    handleInputChange(event) {
      if (this.$store.state.stateAction.activeAmountItemId === this.itemId) {
        this.$store.commit("setActiveAmountItemId", null);
      }
      this.amountValue = +event.target.value;
      this.amountValue = this.clampAmount(this.amountValue);
      this.emitAmountValue();
    },
  },
};
</script>
<style lang="scss">
@import "@/public/scss/common/input/input-amount.scss";
</style>
